import React, { useState, useEffect } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Spinner, Badge } from "components/ui";
import { getIntegrationsByType } from "services/integrationService";
import { toast } from "sonner";

/**
 * Reusable Email Service Selector Modal Component
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onSelect - Callback when service is selected (receives selected service)
 * @param {Object} props.selectedService - Currently selected service (optional)
 * @param {string} props.title - Modal title (default: "Select Email Service")
 * @param {string} props.description - Modal description (optional)
 */
export const EmailServiceSelector = ({
  isOpen,
  onClose,
  onSelect,
  selectedService = null,
  title = "Select Email Service",
  description = "Choose an email service to send your message"
}) => {
  const [emailServices, setEmailServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempSelected, setTempSelected] = useState(selectedService);

  // Load email services when modal opens
  useEffect(() => {
    if (isOpen) {
      loadEmailServices();
      setTempSelected(selectedService);
    }
  }, [isOpen, selectedService]);

  const loadEmailServices = async () => {
    setLoading(true);
    try {
      const result = await getIntegrationsByType("EMAIL");
      if (result.success) {
        setEmailServices(result.data || []);
      } else {
        toast.error(result.error || "Failed to load email services");
      }
    } catch (error) {
      console.error("Error loading email services:", error);
      toast.error("Failed to load email services");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (!tempSelected) {
      toast.error("Please select an email service");
      return;
    }
    onSelect(tempSelected);
    onClose();
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[210]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-2xl transition-all dark:bg-gray-900">
                {/* Header */}
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                  <div>
                    <DialogTitle as="h3" className="text-lg font-medium text-gray-900 dark:text-white">
                      {title}
                    </DialogTitle>
                    {description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4 max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner className="w-5 h-5 mr-2" />
                      <span className="text-sm text-gray-500">Loading services...</span>
                    </div>
                  ) : emailServices.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-sm text-red-500 dark:text-red-400">
                        No email services available. Please configure an email integration.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {emailServices.map((service) => (
                        <button
                          key={service.IntegrationProductId}
                          onClick={() => setTempSelected(service)}
                          className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                            tempSelected?.IntegrationProductId === service.IntegrationProductId
                              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                              : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                  {service.ProductName}
                                </h4>
                                {service.IsDefault && (
                                  <Badge color="primary" variant="soft" className="text-xs">
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Provider: {service.ProviderName}
                              </p>
                              {service.ProductActive && service.ProviderActive && (
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                  ✓ Active and ready to use
                                </p>
                              )}
                            </div>
                            {tempSelected?.IntegrationProductId === service.IntegrationProductId && (
                              <div className="flex-shrink-0 ml-2">
                                <CheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-3 flex justify-end gap-3">
                  <Button
                    onClick={onClose}
                    variant="soft"
                    className="text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSelect}
                    disabled={!tempSelected || loading}
                    variant="solid"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Select Service
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EmailServiceSelector;
